import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { IApiResponse } from '@core/interfaces/api-response.interface';
import { Observable } from 'rxjs';
import { IUser, IUserUpdate } from '../interfaces/user.interface';

@Injectable({
  providedIn: 'root',
})
export class UserService {
  constructor(private readonly http: HttpClient) {}

  public getCurrentUser(): Observable<IUser> {
    return this.http.get<IUser>('users/current');
  }

  public getCustomToken(): Observable<string> {
    return this.http.get<string>('users/custom-token');
  }

  public updateUser(id: string, userUpdate: IUserUpdate): Observable<IUser> {
    return this.http.put<IUser>(`users/${id}`, userUpdate);
  }

  public getUsersAdmin(params?: HttpParams): Observable<IApiResponse<IUser[]>> {
    return this.http.get<IApiResponse<IUser[]>>('users/admin', { params });
  }

  public getUsers(params?: HttpParams): Observable<IApiResponse<IUser[]>> {
    return this.http.get<IApiResponse<IUser[]>>('users', { params });
  }

  public getUser(id: string, params?: HttpParams): Observable<IUser> {
    return this.http.get<IUser>(`users/${id}`, { params });
  }

  public sendPasswordResetEmail(email: string): Observable<void> {
    return this.http.post<void>('users/forgot-password', { email });
  }

  /** Removes one person from the given group's membership list specifically, leaving any other
   * group they belong to untouched. This is what the Users tab's row-level delete action calls:
   * removing a user from *this* group's membership, not deleting their account outright. */
  public removeUserGroupMember(userGroupId: string, userId: string): Observable<void> {
    return this.http.delete<void>(`user-groups/${userGroupId}/members/${userId}`);
  }

  /** Assigns (or replaces) a member's role within a specific group - `role` is either the
   * built-in `campaign_sub` or a custom role id from `GET /roles`. */
  public setUserGroupRole(userId: string, userGroupId: string, role: string): Observable<IUser> {
    return this.http.put<IUser>(`users/${userId}/user-groups/${userGroupId}/role/${role}`, {});
  }

  /** Assigns a member to a team within a group. */
  public setUserGroupTeam(userId: string, userGroupId: string, teamId: string): Observable<IUser> {
    return this.http.put<IUser>(`users/${userId}/user-groups/${userGroupId}/team/${teamId}`, {});
  }

  /** Clears a member's team assignment - they stay in the group, just unassigned. */
  public clearUserGroupTeam(userId: string, userGroupId: string): Observable<IUser> {
    return this.http.delete<IUser>(`users/${userId}/user-groups/${userGroupId}/team`);
  }
}
